import type { AgeGateEnum } from '@iheartjane/data-access';
import { Button, Flex, Modal, TextField, Typography } from '@iheartjane/reefer';
import { getCookieFromDocument } from '@iheartjane/utils';
import { useMaskito } from '@maskito/react';
import { differenceInYears, isAfter, parse } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ReeferCompatForm } from '@jane/bloom/components/reeferCompatForm';
import { useFieldErrorVisibility } from '@jane/bloom/hooks/forms/useFieldErrorVisibility';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import {
  confirmAge,
  getAgeGateCookieName,
} from '@jane/bloom/utils/cookies/ageGate';
import {
  BIRTHDAY_REGEX,
  BIRTH_DATE_MASK,
} from '@jane/bloom/utils/dates/constants';
import { useAgeGatePreview } from '@jane/bloom/utils/queryParams/useAgeGatePreview';

import styles from './ageGate.module.css';

interface AgeGateProps {
  initiallyOpen: boolean;
  type?: AgeGateEnum;
}

export const AgeGate = ({ initiallyOpen, type = 'yes_no' }: AgeGateProps) => {
  const { cookiePrefix, logo, pinnedStore, theme } = useRootLoaderData();
  const ageGateCookieName = useMemo(
    () => getAgeGateCookieName(theme, type, cookiePrefix),
    [theme, type, cookiePrefix]
  );
  const { removeAgeGatePreview, shouldPreviewAgeGate } = useAgeGatePreview();
  const [isIllegalAge, setIsIllegalAge] = useState<boolean>(false);
  const birthdateRef = useMaskito({ options: BIRTH_DATE_MASK });
  const { getShowError, setShowError } = useFieldErrorVisibility([
    { id: 'birthdate' },
  ]);

  const ageGateMsg =
    theme.bloom?.age_gate?.[type]?.message ??
    'Are you over 21 years old or a valid medical cannabis patient?';

  const buttonOrder = theme.bloom?.age_gate?.yes_no?.button_order ?? 'no_yes';

  const onClose = useCallback(() => {
    window.location.href = 'https://www.google.com';
  }, []);

  const isCookieInClient = useMemo(() => {
    if (!initiallyOpen) {
      return true;
    }

    const ageGateCookie = getCookieFromDocument(ageGateCookieName);
    return !!ageGateCookie;
  }, [initiallyOpen, ageGateCookieName]);

  const displayAgeGate = useMemo(
    () => shouldPreviewAgeGate || (initiallyOpen && !isCookieInClient),
    [initiallyOpen, isCookieInClient, shouldPreviewAgeGate]
  );

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      birthdate: '',
    },
    mode: 'all',
  });

  const onSubmit = useCallback(() => {
    if (!isValid) {
      setShowError('birthdate', true);
      return;
    }
    removeAgeGatePreview();
    confirmAge(ageGateCookieName, theme.bloom?.age_gate?.expires_after);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageGateCookieName, removeAgeGatePreview, isValid]);

  return (
    <Modal
      variant="dialogue"
      onRequestClose={onClose}
      open={displayAgeGate}
      className={styles['modal']}
      overlayClassName="blurred-overlay"
      parentId="age-gate"
    >
      <Modal.Content className={styles['modalContent']}>
        {logo?.xsmall && (
          <div className={styles['logoContainer']}>
            <img
              src={logo.xsmall}
              alt={pinnedStore.name}
              className={styles['logo']}
              loading="eager"
            />
          </div>
        )}
        <div className={styles['message']}>
          <Typography color="inherit" variant="body-semibold">
            {ageGateMsg}
          </Typography>
        </div>

        {type === 'date_picker' ? (
          <ReeferCompatForm name="age gate" onSubmit={handleSubmit(onSubmit)}>
            <div className={styles['ageGateform']}>
              <Controller
                name="birthdate"
                control={control}
                rules={{
                  pattern: {
                    message: 'Please enter a valid Date of Birth.',
                    value: BIRTHDAY_REGEX,
                  },
                  required: 'Please enter a valid Date of Birth.',
                  validate: (date: string | undefined) => {
                    if (!date) {
                      return 'Please enter a valid Date of Birth.';
                    }
                    const parsed = parse(date, 'MM/dd/yyyy', new Date());
                    if (
                      isNaN(parsed.getTime()) ||
                      isAfter(parsed, new Date())
                    ) {
                      return 'Please enter a valid Date of Birth.';
                    }
                    if (!isAfter(parsed, new Date(1900, 0, 1))) {
                      return 'Birth date must be after Jan 1, 1900';
                    }
                    const today = new Date();
                    const age = differenceInYears(today, parsed);

                    if (age < 21) {
                      setIsIllegalAge(true);
                      return 'You must be at least 21 years old.';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    disableMobileInputStyling
                    label=""
                    onBlur={() => {
                      setShowError('birthdate', true);
                    }}
                    ref={birthdateRef}
                    name="birthdateInput"
                    placeholder="MM/DD/YYYY"
                    borderRadius="lg"
                    autocomplete="off"
                    width="160px"
                    mt={-32}
                    mx="auto"
                    mb={-12}
                    className={styles['dobInput']}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
              {errors.birthdate && getShowError('birthdate') && (
                <Typography variant="small-body-regular" color="error">
                  {errors.birthdate.message}
                </Typography>
              )}
            </div>
            <Button
              type="submit"
              label="Confirm"
              onClick={isIllegalAge ? onClose : onSubmit}
              mt={24}
              full
              disabled={!isValid}
              variant="primary"
            />
          </ReeferCompatForm>
        ) : (
          <Flex
            flexDirection={buttonOrder === 'yes_no' ? 'row-reverse' : 'row'}
            gap={12}
          >
            <Button
              className={styles['decline']}
              full
              variant="tertiary"
              label="No"
              onClick={onClose}
              type="submit"
            />
            <Button
              full
              variant="primary"
              label="Yes"
              onClick={onSubmit}
              type="submit"
              data-testid="confirm-age"
            />
          </Flex>
        )}
      </Modal.Content>
    </Modal>
  );
};
